import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { formBuilderApi, templateConfigKeys } from '@/features/quote-and-apply-builder';

const EMPTY_CONDITIONAL: TemplateConditional = { rules: [], logic: 'AND' };

interface UseTemplateConfigFormProps {
  formId: string;
  config?: PolicyApplicationTemplateConfiguration | null;
  onClose: () => void;
}

export function useTemplateConfigForm({ formId, config, onClose }: UseTemplateConfigFormProps) {
  const queryClient = useQueryClient();
  const isEditing = !!config;

  const [templateName, setTemplateName] = useState(config?.templateName ?? '');
  const [defaultSequence, setDefaultSequence] = useState(config?.defaultSequence ?? 1);
  const [isRequired, setIsRequired] = useState(config?.isRequired ?? true);
  const [allowedRoles, setAllowedRoles] = useState<string[]>(config?.allowedRoles ?? []);
  const [conditional, setConditional] = useState<TemplateConditional>(
    config?.conditional && config.conditional.rules?.length > 0
      ? config.conditional
      : EMPTY_CONDITIONAL,
  );
  const [status, setStatus] = useState<'active' | 'inactive'>(config?.status ?? 'active');

  const invalidateConfigs = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: templateConfigKeys.detail(formId) });
  }, [queryClient, formId]);

  const { mutate: createConfig, isPending: isCreating } = useMutation({
    mutationFn: formBuilderApi.createTemplateConfigs,
    onSuccess: () => invalidateConfigs(),
  });

  const { mutate: updateConfig, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, params }: { id: string; params: Parameters<typeof formBuilderApi.updateTemplateConfig>[1] }) =>
      formBuilderApi.updateTemplateConfig(id, params),
    onSuccess: () => invalidateConfigs(),
  });

  const isSaving = isCreating || isUpdating;

  const canSave = templateName.trim().length > 0;

  const handleToggleRole = useCallback((role: string) => {
    setAllowedRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role],
    );
  }, []);

  const handleAddRule = useCallback(() => {
    setConditional(prev => ({
      ...prev,
      rules: [...prev.rules, { field: '', operator: 'equals' as const, value: '' }],
    }));
  }, []);

  const handleUpdateRule = useCallback(
    (index: number, updates: Partial<TemplateConditionalRule>) => {
      setConditional(prev => ({
        ...prev,
        rules: prev.rules.map((rule, i) => (i === index ? { ...rule, ...updates } : rule)),
      }));
    },
    [],
  );

  const handleRemoveRule = useCallback((index: number) => {
    setConditional(prev => ({
      ...prev,
      rules: prev.rules.filter((_, i) => i !== index),
    }));
  }, []);

  const handleSetLogic = useCallback((logic: TemplateConditional['logic']) => {
    setConditional(prev => ({ ...prev, logic }));
  }, []);

  const handleSave = useCallback(() => {
    if (!canSave) return;

    const conditionalPayload =
      conditional.rules.length > 0 ? conditional : EMPTY_CONDITIONAL;

    if (isEditing && config) {
      updateConfig(
        {
          id: config.id,
          params: {
            templateName: templateName.trim(),
            defaultSequence,
            isRequired,
            allowedRoles,
            conditional: conditionalPayload,
            status,
          },
        },
        {
          onSuccess: () => {
            toast.success(`Template "${templateName.trim()}" updated`);
            onClose();
          },
          onError: err => {
            toast.error(err instanceof Error ? err.message : 'Failed to update template');
          },
        },
      );
    } else {
      createConfig(
        {
          insuranceCarrierProductPolicyApplicationFormId: formId,
          templates: [
            {
              templateName: templateName.trim(),
              defaultSequence,
              isRequired,
              allowedRoles,
              conditional: conditionalPayload,
              status,
            },
          ],
        },
        {
          onSuccess: () => {
            toast.success(`Template "${templateName.trim()}" created`);
            onClose();
          },
          onError: err => {
            toast.error(err instanceof Error ? err.message : 'Failed to create template');
          },
        },
      );
    }
  }, [
    canSave,
    conditional,
    isEditing,
    config,
    updateConfig,
    templateName,
    defaultSequence,
    isRequired,
    allowedRoles,
    status,
    createConfig,
    formId,
    onClose,
  ]);

  return {
    isEditing,
    templateName,
    setTemplateName,
    defaultSequence,
    setDefaultSequence,
    isRequired,
    setIsRequired,
    allowedRoles,
    handleToggleRole,
    conditional,
    handleAddRule,
    handleUpdateRule,
    handleRemoveRule,
    handleSetLogic,
    status,
    setStatus,
    isSaving,
    canSave,
    handleSave,
  };
}
